<template>
  <div class="fbm-thresholds-inline">
    <div class="fbm-inline-title">
      <i class="cog icon"></i>
      <span>Alert Threshold — {{ productTitle }}</span>
    </div>

    <div class="fbm-inline-fields">
      <div class="fbm-mkt-group">
        <div class="fbm-mkt-group-label">
          <i :class="getMarketplaceFlag(item && item.marketplace ? item.marketplace : null) + ' flag'"></i>
          <span>{{ marketplaceLabel }}</span>
        </div>
        <div class="fbm-mkt-fields">
          <div class="fbm-inline-field">
            <label>Alert below</label>
            <input
              v-model.number="form.redThreshold"
              type="number"
              min="0"
              placeholder="—"
              :disabled="!resolvedProductId"
              :class="['fbm-inline-input', fbmInputFilled]"
            />
            <span class="fbm-field-unit">units</span>
          </div>
        </div>
      </div>
    </div>

    <div class="fbm-inline-actions">
      <button
        type="button"
        class="fbm-btn-save"
        :disabled="saving || !resolvedProductId"
        @click="save"
      >
        Save
      </button>
      <button type="button" class="fbm-btn-cancel" @click="cancel">Cancel</button>
      <span class="fbm-inline-hint">Alert fires when your FBM stock drops below this level.</span>
    </div>

    <div v-if="saveError" class="fbm-inline-error">{{ saveError }}</div>
    <div v-if="saving" class="fbm-inline-loading">
      <i class="notched circle loading icon"></i> Saving…
    </div>
  </div>
</template>

<script>
import {
  listingIdFromProduct,
  fbmThresholdFromSettings,
  extractThresholdGetItems,
  productIdFromThresholdRow
} from '../../utils/stockAlertThresholdsApiParse'

export default {
  name: 'FBMAlertThresholdsInline',
  props: {
    item: { type: Object, default: null },
    /** Resolved listing id (same as Stock page / stock-alert-thresholds API). */
    productId: { type: [String, Number], default: null },
    /** When Issues navbar has exactly one seller account selected, scope PATCH. */
    sellerAccountId: { type: String, default: null },
    productTitle: { type: String, default: '' },
    /** FBM: silent GET on open + PATCH on Save. Watchdog `item.settings` may omit listing overrides; stock API is source of truth. */
    stockThresholdsApiBase: { type: String, default: '/api/v1/stock-alert-thresholds' },
    /** @deprecated Retained for compatibility; FBM thresholds use stockThresholdsApiBase. */
    apiBaseUrl: { type: String, default: '/api/v1/watchdogs' }
  },
  data: function () {
    return {
      form: {
        redThreshold: null
      },
      saving: false,
      saveError: null
    }
  },
  computed: {
    marketplaceLabel: function () {
      var m = (this.item && this.item.marketplace) ? this.item.marketplace.toUpperCase() : ''
      var map = { US: 'US', UK: 'UK', CA: 'CA', DE: 'DE', FR: 'FR', ES: 'ES', IT: 'IT', MX: 'MX', BR: 'BR', IN: 'IN', NL: 'NL' }
      return map[m] || m
    },
    resolvedProductId: function () {
      if (this.productId != null && this.productId !== '') return String(this.productId)
      return this.item ? listingIdFromProduct(this.item) : ''
    },
    fbmInputFilled: function () {
      var v = this.form.redThreshold
      return (v !== null && v !== undefined && v !== '' && !isNaN(Number(v))) ? 'has-value' : ''
    }
  },
  watch: {
    item: {
      handler: function () {
        this.loadFromItemFallback()
      },
      immediate: true
    },
    productId: function () {
      this.loadFromItemFallback()
      this.hydrateFromStockApiSilent()
    }
  },
  mounted: function () {
    this.hydrateFromStockApiSilent()
  },
  methods: {
    httpParams: function () {
      var p = {}
      if (this.sellerAccountId) p.seller_account_id = this.sellerAccountId
      return p
    },
    getMarketplaceFlag: function (marketplace) {
      if (!marketplace) return 'flag'
      var m = marketplace.toLowerCase()
      return (m === 'uk' ? 'gb' : m).replace('uk', 'gb')
    },
    /** Immediate fill from watchdog row; may be empty if backend does not mirror FBM overrides here. */
    loadFromItemFallback: function () {
      var s = this.item && this.item.settings ? this.item.settings : {}
      this.form = {
        redThreshold: fbmThresholdFromSettings(s)
      }
    },
    /** No loading UI: replace form when stock-alert-thresholds returns this listing (matches Stock FBM tab). */
    hydrateFromStockApiSilent: function () {
      var pid = this.resolvedProductId
      if (!pid) return
      var self = this
      this.$http.get(this.stockThresholdsApiBase, {
        params: Object.assign({}, this.httpParams(), {
          channel: 'fbm',
          product_ids: pid
        }),
        timeout: 45000
      }).then(function (res) {
        var items = extractThresholdGetItems(res.body)
        var i
        for (i = 0; i < items.length; i++) {
          var row = items[i]
          if (productIdFromThresholdRow(row) !== pid) continue
          var raw = row.settings
          if (raw == null) raw = {}
          else if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw)
            } catch (e) {
              raw = {}
            }
          }
          var settings = typeof raw === 'object' && raw !== null ? raw : {}
          self.form = { redThreshold: fbmThresholdFromSettings(settings) }
          return
        }
      }, function () {
        /* keep loadFromItemFallback values */
      })
    },
    toPayload: function () {
      var v = this.form.redThreshold
      var threshold = (v !== null && v !== '' && !isNaN(Number(v))) ? Number(v) : null
      return { threshold: threshold }
    },
    applySavedPayloadToItem: function (settings) {
      var item = this.item
      if (!item) return
      var t = settings.threshold
      if (!item.settings) this.$set(item, 'settings', {})
      this.$set(item.settings, 'threshold', t)
      this.$set(item.settings, 'redThreshold', t)
      this.$set(item.settings, 'red_threshold', t)
    },
    save: function () {
      var pid = this.resolvedProductId
      if (!pid) return
      this.saving = true
      this.saveError = null
      var settings = this.toPayload()
      var body = {
        channel: 'fbm',
        updates: [{ product_id: pid, settings: settings }]
      }
      var self = this
      this.$http.patch(this.stockThresholdsApiBase, body, { params: this.httpParams(), timeout: 60000 }).then(function (res) {
        self.saving = false
        var failed = res.body && res.body.failed
        if (failed > 0) {
          self.saveError = 'Some updates failed. Check results from the server.'
          return
        }
        self.applySavedPayloadToItem(settings)
        self.$emit('saved')
      }, function (err) {
        self.saving = false
        self.saveError = (err.body && err.body.message) || err.statusText || 'Failed to save settings'
      })
    },
    cancel: function () {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.fbm-thresholds-inline {
  padding: 16px 20px;
  border-top: 1px solid var(--border-light);
  background: #f8faff;
}

.fbm-inline-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-navy-dark, #143a66);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.fbm-inline-title .icon {
  margin: 0;
}

.fbm-inline-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  align-items: flex-end;
}

.fbm-mkt-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fbm-mkt-group-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.fbm-mkt-fields {
  display: flex;
  gap: 10px;
}

.fbm-inline-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fbm-inline-field label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.fbm-inline-input {
  width: 88px;
  padding: 6px 8px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-white);
}

.fbm-inline-input:focus {
  outline: none;
  border-color: var(--primary-navy-dark, #143a66);
  box-shadow: 0 0 0 2px rgba(27, 75, 130, 0.1);
}

.fbm-inline-input.has-value {
  border-color: var(--primary-navy-light, #2a5f9e);
  background: #eff4fb;
  font-weight: 600;
}

.fbm-field-unit {
  font-size: 10px;
  color: var(--text-tertiary);
  text-align: center;
  margin-top: 2px;
}

.fbm-inline-actions {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.fbm-btn-save {
  padding: 8px 20px;
  background: var(--accent-orange);
  border: none;
  border-radius: 7px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.fbm-btn-save:hover:not(:disabled) {
  background: var(--accent-orange-hover);
}

.fbm-btn-save:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.fbm-btn-cancel {
  padding: 8px 16px;
  background: none;
  border: 1px solid var(--border-medium);
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  color: var(--text-secondary);
}

.fbm-btn-cancel:hover {
  border-color: var(--text-secondary);
  color: var(--text-primary);
}

.fbm-inline-hint {
  font-size: 11px;
  color: var(--text-tertiary);
}

.fbm-inline-error {
  margin-top: 8px;
  color: var(--status-critical);
  font-size: 13px;
}

.fbm-inline-loading {
  margin-top: 8px;
  margin-bottom: 4px;
  color: var(--text-secondary);
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// FBMAlertThresholdsInline.vue?7c505f40